import React, { useEffect } from "react";
// ANT DESIGN COMPONENTS
import { DatePicker, Form, Modal, Select, Typography, message } from "antd";
// antd 4 pickers take moment values, as every other DatePicker here does.
import moment, { Moment } from "moment";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  EdgeUpdateRing,
  useCreateEdgeUpdateAssignmentMutation,
  useGetEdgeUpdateReleasesQuery,
} from "../../../redux/servers/serverAPI";
import { updateOpenModalUpdateServer } from "../../../redux/servers/serverSlice";
// INTERFACES
import { RootState } from "../../../app/store";
// UTILS
import { apiErrorMessage } from "../../../utils/apiErrorMessage";
import { uuidToString } from "../../../utils/uuid";

const RINGS: EdgeUpdateRing[] = ["lab", "canary", "pilot", "general"];
const UPDATE_WINDOW_HOURS = 3;

interface UpdateFormValues {
  release_id: string;
  ring: EdgeUpdateRing;
  deadline: Moment;
}

function ServersUpdateModal() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");
  const [form] = Form.useForm<UpdateFormValues>();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { server, openModalUpdateServer } = useSelector((state: RootState) => state.server);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const { data: releases, isLoading: isLoadingReleases } = useGetEdgeUpdateReleasesQuery(
    undefined,
    { skip: !openModalUpdateServer },
  );
  const [assign, { isLoading: isAssigning }] = useCreateEdgeUpdateAssignmentMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const versions: Record<string, string> = {};
  (releases?.data ?? []).forEach((release) => {
    versions[release.id] = release.version;
  });

  const onClickClose = () => {
    dispatch(updateOpenModalUpdateServer(false));
  };

  const onFinish = async (values: UpdateFormValues) => {
    try {
      await assign({
        server_id: uuidToString(server.id),
        release_id: values.release_id,
        ring: values.ring,
        not_before: new Date().toISOString(),
        deadline: values.deadline.toISOString(),
      }).unwrap();
      message.success(
        t("edgeServerUpdate.success", {
          name: String(server.name ?? ""),
          version: versions[values.release_id],
        }),
      );
      form.resetFields();
      onClickClose();
    } catch (requestError) {
      message.error(apiErrorMessage(requestError, t("edgeServerUpdate.error")));
    }
  };

  // The form instance outlives the modal (destroyOnClose unmounts only the
  // fields), so every opening starts from fresh defaults. Without this a
  // cancelled choice for one server, and a deadline computed hours earlier,
  // carried over to the next server. Runs before the preselect below.
  useEffect(() => {
    if (openModalUpdateServer) {
      form.resetFields();
      form.setFieldsValue({ ring: RINGS[0], deadline: moment().add(UPDATE_WINDOW_HOURS, "hours") });
    }
  }, [openModalUpdateServer, form]);

  // Preselect the newest selectable release once the list arrives.
  useEffect(() => {
    if (openModalUpdateServer && releases && !form.getFieldValue("release_id")) {
      const newest = releases.data.find((release) => !release.paused_at);
      if (newest) form.setFieldValue("release_id", newest.id);
    }
  }, [openModalUpdateServer, releases, form]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      title={t("edgeServerUpdate.title", { name: String(server.name ?? "") })}
      open={openModalUpdateServer}
      okText={t("edgeServerUpdate.assign")}
      confirmLoading={isAssigning}
      onOk={form.submit}
      onCancel={onClickClose}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        {t("edgeServerUpdate.description")}
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="release_id"
          label={t("edgeServerUpdate.release")}
          rules={[{ required: true, message: t("edgeServerUpdate.releaseRequired") }]}
        >
          <Select
            loading={isLoadingReleases}
            placeholder={t("edgeServerUpdate.releasePlaceholder")}
            options={(releases?.data ?? []).map((release) => ({
              value: release.id,
              disabled: Boolean(release.paused_at),
              label: `${release.version} · #${release.sequence}${
                release.paused_at ? ` · ${t("edgeServerUpdate.releasePaused")}` : ""
              }`,
            }))}
          />
        </Form.Item>
        <Form.Item name="ring" label={t("edgeServerUpdate.ring")}>
          <Select
            options={RINGS.map((ring) => ({
              value: ring,
              label: t(`edgeServerUpdate.rings.${ring}`),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="deadline"
          label={t("edgeServerUpdate.deadline")}
          rules={[
            { required: true, message: t("edgeServerUpdate.deadlineRequired") },
            {
              validator: (_rule, value?: Moment) =>
                !value || value.isAfter(moment())
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("edgeServerUpdate.deadlinePast"))),
            },
          ]}
        >
          <DatePicker
            showTime
            style={{ width: "100%" }}
            disabledDate={(day) => day.isBefore(moment(), "day")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default ServersUpdateModal;
